import { useState } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  TimePicker,
  Select,
  Button,
  Typography,
  Space,
  notification,
  Row,
  Col,
  Card,
} from "antd";
import {
  CalendarOutlined,
  WhatsAppOutlined,
  UserOutlined,
  MailOutlined,
  BankOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// Zambian theme colors
const ZAMBIA_THEME = {
  primary: "#078930",
  secondary: "#EF3340", 
  accent: "#FFB81C",
  neutral: "#2D2D2D",
};

const DemoBookingModal = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Format the demo booking data
      const demoData = {
        full_name: values.full_name,
        email: values.email,
        whatsapp_number: values.whatsapp_number,
        company_name: values.company_name,
        company_type: values.company_type,
        preferred_date: values.preferred_date.format('YYYY-MM-DD'),
        preferred_time: values.preferred_time.format('HH:mm'),
        notes: values.notes,
        status: "pending",
        created_at: moment().format('YYYY-MM-DD HH:mm:ss')
      };

      // Create calendar event (simplified for now)
      const eventData = {
        summary: `Demo: ${demoData.company_name} - ${demoData.full_name}`,
        description: `Demo booking for ${demoData.company_name}\n\nContact: ${demoData.full_name}\nEmail: ${demoData.email}\nWhatsApp: ${demoData.whatsapp_number}\nCompany Type: ${demoData.company_type}\n\nNotes: ${demoData.notes || 'No additional notes'}`,
        start_date: `${demoData.preferred_date} ${demoData.preferred_time}`,
        attendees: [demoData.email],
        whatsapp_number: demoData.whatsapp_number
      };

      // Save to your database (replace with your actual API endpoint)
      await axios.post('https://zedlenders.pickmesms.com/items/demo_bookings', demoData, {
        headers: {
          'Content-Type': 'application/json',
        }
      });

      // Here you would integrate with Google Calendar API
      // For now, we'll just show success and notify you manually
      console.log('Calendar Event Data:', eventData);

      notification.success({
        message: 'Demo Scheduled Successfully!',
        description: `Thank you ${demoData.full_name}! We'll send you a WhatsApp message with the meeting details shortly.`,
        duration: 6,
      });

      form.resetFields();
      onClose();
    } catch (error) {
      console.error('Demo booking error:', error);
      notification.error({
        message: 'Booking Failed',
        description: 'There was an error scheduling your demo. Please try again or contact us directly.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <CalendarOutlined style={{ color: ZAMBIA_THEME.primary, fontSize: '24px' }} />
          <Title level={3} style={{ margin: 0, color: ZAMBIA_THEME.primary }}>
            Schedule Your Personal Demo
          </Title>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      style={{ top: 20 }}
    >
      <div style={{ marginBottom: 24 }}>
        <Card 
          size="small" 
          style={{ 
            background: `linear-gradient(135deg, ${ZAMBIA_THEME.primary}10, ${ZAMBIA_THEME.accent}10)`,
            border: `1px solid ${ZAMBIA_THEME.primary}30`
          }}
        >
          <Paragraph style={{ margin: 0, textAlign: 'center', fontSize: 16 }}>
            🚀 <Text strong>Get a personalized demo</Text> of Kwacha Plus and see how it can transform your lending operations!
          </Paragraph>
        </Card>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          preferred_date: moment().add(1, 'day'),
          preferred_time: moment().set({ hour: 14, minute: 0 })
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Full Name"
              name="full_name"
              rules={[{ required: true, message: 'Please enter your full name' }]}
            >
              <Input 
                prefix={<UserOutlined />} 
                placeholder="Enter your full name" 
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Email Address"
              name="email"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Please enter a valid email' }
              ]}
            >
              <Input 
                prefix={<MailOutlined />} 
                placeholder="your.email@company.com" 
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="WhatsApp Number"
              name="whatsapp_number"
              rules={[{ required: true, message: 'Please enter your WhatsApp number' }]}
            >
              <Input 
                prefix={<WhatsAppOutlined style={{ color: '#25D366' }} />} 
                placeholder="+260 XXX XXX XXX" 
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Company Name"
              name="company_name"
              rules={[{ required: true, message: 'Please enter your company name' }]}
            >
              <Input 
                prefix={<BankOutlined />} 
                placeholder="Your Company Name" 
                size="large"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Company Type"
          name="company_type"
          rules={[{ required: true, message: 'Please select your company type' }]}
        >
          <Select placeholder="Select your company type" size="large">
            <Select.Option value="bank">Commercial Bank</Select.Option>
            <Select.Option value="microfinance">Microfinance Institution</Select.Option>
            <Select.Option value="savings_credit">Savings & Credit Cooperative</Select.Option>
            <Select.Option value="ngo">NGO/Development Organization</Select.Option>
            <Select.Option value="fintech">Fintech Startup</Select.Option>
            <Select.Option value="other">Other Financial Institution</Select.Option>
          </Select>
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Preferred Date"
              name="preferred_date"
              rules={[{ required: true, message: 'Please select a preferred date' }]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                size="large"
                disabledDate={(current) => current && current < moment().startOf('day')}
                format="YYYY-MM-DD"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Preferred Time"
              name="preferred_time"
              rules={[{ required: true, message: 'Please select a preferred time' }]}
            >
              <TimePicker 
                style={{ width: '100%' }} 
                size="large"
                format="HH:mm"
                minuteStep={30}
                showNow={false}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Additional Notes (Optional)"
          name="notes"
        >
          <TextArea 
            rows={3} 
            placeholder="Tell us about your specific needs or questions..."
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item>
          <Space style={{ width: '100%', justifyContent: 'center' }} size="large">
            <Button size="large" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              icon={<CalendarOutlined />}
              style={{
                background: ZAMBIA_THEME.primary,
                borderColor: ZAMBIA_THEME.primary,
                padding: '0 40px',
                height: '45px',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              Schedule Demo
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <div style={{ 
        marginTop: 20, 
        padding: 16, 
        background: '#f9f9f9', 
        borderRadius: 8, 
        textAlign: 'center' 
      }}>
        <Space direction="vertical" size="small">
          <Text type="secondary">
            <ClockCircleOutlined /> We'll confirm your demo within 24 hours via WhatsApp
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Demo sessions typically last 30-45 minutes and are completely free
          </Text>
        </Space>
      </div>
    </Modal>
  );
};

export default DemoBookingModal;